import React, {FC} from 'react';
import styled from 'styled-components';

import {RatingSummary, Screenshot} from '../../types/app';
import Icon from './Icon';
import {OneStarRating} from './Rating/OneStarRating';
import {Link} from '../Common';
import {fontSemibold} from '../../themes';

import arrowRight from '../../icons/arrow-right.svg';
import icComplete from '../../icons/ic_complete.png';
import {BannerContainer} from './ImageContainer/BannerContainer';
import {AppType} from '../../constants';
import {DEVICE_PLACEHOLDERS, DEVICE_RENAME, hidenCollectionsIds} from 'config';
import {connect} from 'react-redux';
import { TileContainer, TileImage } from './ImageContainer/TileImage';

const DEVICES: any = DEVICE_PLACEHOLDERS;
const RENAME: any = DEVICE_RENAME;

type ReduxProps = ReturnType<typeof mapStateToProps>;

interface AppCardProps extends ReduxProps {
  id: string;
  name: string;
  rating?: RatingSummary;
  screenshot: Screenshot;
  appStatus?: any;
  appType?: AppType;
  showRatings: boolean;
  isPremium: boolean;
  deviceName: string;
  linkTo?: string;
  parentAppName?: string;
  collectionId?: string;
  onClick?: () => void;
}

const DEFAULT_TILE_WIDTH = 40;
const DEFAULT_TILE_WIDTH_BEZELS = 46;

const Container = styled.div`
  display: flex;
  padding: 15px 15px 15px 5px;
`;

const AppStatusContainer = styled.div`
  padding: 15px;
`

const BannerDiv = styled.div`
  width: 50px;
  height: 50px;
  flex-shrink: 0;
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CenterContent = styled.div`
  flex-grow: 1;
  min-width: 0px;
  padding: 0 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Name = styled.div`
  font-family: ${fontSemibold};
  padding: 3px 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Ratings = styled.div`
  display: flex;
`;

const ParentAppNameContainer = styled.div`
  display: flex;
`;

const ParentAppName = styled.div`
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.15px;

  color: #707C83;
`;

const ArrowContainer = styled.div`
  display: flex;
`;

const AppCard: FC<AppCardProps> = ({
  id,
  screenshot,
  rating,
  name,
  appStatus,
  showRatings,
  isPremium,
  deviceName,
  linkTo,
  parentAppName,
  collectionId,
  appType,
  gregEnabled,
  optimizelyUser,
  installedTiles,
  onClick
}) => {

  const queryParam = collectionId ? `?collectionId=${collectionId}` : '';

  let type = 'app';
  let isTile = false;

  if(appType && (appType.toLowerCase() === AppType.TILE)) {
    type = AppType.TILE
    isTile = true;
  }

  let premiumTileIcon;
  if (isPremium) {
    premiumTileIcon = DEVICES[deviceName]?.premiumTileIcon;
  }

  return (
    <Container>
      <Link
        to={ linkTo ? linkTo :`/v2/${type}/${id}${queryParam}`}
        pageDirection="FORWARD"
        aria-label={id}
        id={id}
        style={{ width: '100%' }}
        onClick={() => {
          if ( gregEnabled && collectionId && hidenCollectionsIds.includes(collectionId) && optimizelyUser ) {
            optimizelyUser.trackEvent('greg_app_open');
          }
          onClick && onClick()
        }}
      >
        <Content>
          {isTile ?
            (
              <TileContainer
                premiumTileIcon={premiumTileIcon}
                isTile={isTile}
                size={screenshot}
                tileBezelWidth={DEFAULT_TILE_WIDTH_BEZELS}
              >
                <TileImage
                  image={screenshot}
                  name={name}
                  tileWidth={DEFAULT_TILE_WIDTH}
                />
              </TileContainer>
            ) : (
              <BannerDiv>
                <BannerContainer
                    containerAttr={{
                      width: screenshot.width,
                      height: screenshot.height,
                    } as any}
                    imageAttr={{
                      src: screenshot.uri,
                      alt: name,
                    }}
                />
              </BannerDiv>
            )
          }
          <CenterContent>
            <Name>{name}</Name>
            <Ratings>
              { showRatings && <OneStarRating rating={rating || { numRatings: 0, averageStars: 0 }} /> }
            </Ratings>
            <ParentAppNameContainer>
              { parentAppName && <ParentAppName> { parentAppName } </ParentAppName> }
            </ParentAppNameContainer>
          </CenterContent>
          { appStatus && <AppStatusContainer>{appStatus}</AppStatusContainer> }
          <ArrowContainer>
            {
              isTile && Object.keys(installedTiles || {}).includes(id) ? (
                <Icon
                  id={`ic-complete-${id}`}
                  aria-label={`ic-complete-${id}`}
                  url={icComplete}
                  width={22}
                  height={22}
                />
              ) : (
                <Icon
                  id={`app-arrow-${id}`}
                  aria-label={`app-arrow-${id}`}
                  url={arrowRight}
                  width={20}
                  height={20}
                />
              )
            }
          </ArrowContainer>
        </Content>
      </Link>
    </Container>
  );
}

const mapStateToProps = (state: any) => {
  const deviceName = state.registry.currentUser.device?.productName?.toLowerCase();

  const { installedTiles } = state.registry

  const galleryState = state.gallery;
  const { gregEnabled, userContext } = galleryState.experiments;

  return {
    deviceName: RENAME[deviceName] || deviceName,
    gregEnabled,
    optimizelyUser: userContext,
    installedTiles
  }
};

const ConnectedAppCard = connect(mapStateToProps)(AppCard);

export {ConnectedAppCard as AppCard};
